import { NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  ViewEncapsulation,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import {
  Document,
  withPreviewFileTypes,
  PicturesViewerData,
} from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { ButtonComponent, IconComponent } from '@sc/ui-components';
import { NgxImageZoomModule } from 'ngx-image-zoom';

@Component({
  selector: 'sc-pictures-viewer',
  styleUrls: ['./pictures-viewer.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconComponent,
    ButtonComponent,
    NgIf,
    NgFor,
    TranslateModule,
    NgxImageZoomModule,
  ],
  template: `
    <div class="container">
      <div class="header">
        <h1
          mat-dialog-title
          id="pictures-viewer-title-id"
          class="sc-design-title-h1 sc-design-title-h1-dark"
        >
          {{ getPictureLabel(currentPicture) }}
        </h1>
        <button class="close-cross" (click)="close()">
          <sc-icon name="close"></sc-icon>
          <span class="sr_only">{{
            'GlobalApplicativeConstants.accessibility.closeDialog'
              | translate: { dialogTitle: getPictureLabel(currentPicture) }
          }}</span>
        </button>
        <sc-button (click)="close()" buttonType="mainWhite" leftIcon="close">{{
          'InputPicture.closeBtn' | translate
        }}</sc-button>
      </div>
      <div class="body">
        <button
          class="body-nav navigation previous"
          (click)="currentIndex = previousIndex"
        >
          <sc-icon name="arrow_left"></sc-icon>
          <span class="sr_only">{{
            'InputPicture.previousPic' | translate
          }}</span>
        </button>
        <lib-ngx-image-zoom
          [thumbImage]="currentPicture.url"
          [fullImage]="currentPicture.url"
          [enableScrollZoom]="true"
          *ngIf="currentPicture.url && canPreview(currentPicture)"
        ></lib-ngx-image-zoom>
        <button
          class="body-nav navigation next"
          (click)="currentIndex = nextIndex"
        >
          <sc-icon name="arrow_right"></sc-icon>
          <span class="sr_only">{{ 'InputPicture.nextPic' | translate }}</span>
        </button>
      </div>
      <div class="footer" *ngIf="pictures?.length > 1">
        <button
          class="footer-nav navigation previous"
          (click)="currentIndex = previousIndex"
        >
          <sc-icon name="arrow_left"></sc-icon>
          <span class="sr_only">{{
            'InputPicture.previousPic' | translate
          }}</span>
        </button>
        <div
          *ngFor="let picture of pictures; let index = index"
          [class]="getFooterPictureClass(picture)"
        >
          <img
            *ngIf="picture?.url && canPreview(picture)"
            [src]="picture.url"
            [alt]="picture.url"
            tabindex="0"
            (click)="currentIndex = index"
            (keydown.enter)="currentIndex = index"
            (keydown.space)="currentIndex = index"
          />
          <sc-icon
            *ngIf="!picture?.url || !canPreview(picture)"
            name="unlink"
          ></sc-icon>
        </div>
        <button
          class="footer-nav navigation next"
          (click)="currentIndex = nextIndex"
        >
          <sc-icon name="arrow_right"></sc-icon>
          <span class="sr_only">{{ 'InputPicture.nextPic' | translate }}</span>
        </button>
      </div>
    </div>
  `,
})
export class PicturesViewerComponent {
  pictures: Document[];
  currentIndex = 0;

  constructor(
    private readonly translator: TranslatorService,
    private readonly configService: ConfigurationService,
    @Inject(MAT_DIALOG_DATA) public data: PicturesViewerData,
    public dialogRef: MatDialogRef<PicturesViewerComponent>
  ) {
    this.pictures = data.pictures.filter(
      (picture) =>
        this.configService
          .getSupportedDocumentTypes()
          .findIndex(
            (docType) =>
              picture.type === docType || !!this.data.allowUnsupported
          ) > -1
    );
    this.currentIndex = data.index ?? 0;
  }

  get currentPicture() {
    return this.pictures[this.currentIndex];
  }

  get viewableIndexes() {
    return this.pictures
      .map((pic, i) => (pic?.url && this.canPreview(pic) ? i : null))
      .filter((i) => i !== null);
  }

  get nextIndex() {
    const currentIndexIndex = this.viewableIndexes.findIndex(
      (i) => i === this.currentIndex
    );

    return this.viewableIndexes[
      (currentIndexIndex + 1) % this.viewableIndexes.length
    ];
  }

  get previousIndex() {
    const currentIndexIndex = this.viewableIndexes.findIndex(
      (i) => i === this.currentIndex
    );

    return this.viewableIndexes[
      (currentIndexIndex || this.viewableIndexes.length) - 1
    ];
  }

  canPreview(picture: Document) {
    return withPreviewFileTypes.includes(
      picture.metadata?.mimeType || picture?.file?.type
    );
  }

  getPictureLabel(picture: Document): void {
    return this.translator.getInstantTranslation(
      `GlobalApplicativeConstants.documentType.${picture?.type}`
    );
  }

  getFooterPictureClass(picture: Document) {
    return `picture-button picture-${picture?.type}`;
  }

  close(): void {
    this.dialogRef.close();
  }
}
